// Push-уведомления в браузер (Web Push): подписка этого браузера и её привязка к пользователю на сервере.
import axios from 'axios'
import Vue from 'vue'
import { deviceId, isAuthenticated } from './auth'
import { isStandalone } from './pwa'

export const pushState = Vue.observable({
  // сервер настроен (ключи VAPID заданы)
  serverEnabled: false,
  publicKey: '',
  // этот браузер умеет push (на iPhone — только установленное приложение)
  supported: false,
  // 'default' | 'granted' | 'denied' — разрешение браузера
  permission: 'default',
  // этот браузер подписан
  subscribed: false,
  endpoint: '',
  ready: false
})

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

// Ключ сервера для подписки: base64url → массив байтов
function keyBytes(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const raw = window.atob((base64url + padding).replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

function registration() {
  // воркер регистрируется при загрузке страницы; если его нет — ждать нечего
  return navigator.serviceWorker.getRegistration('/').then((reg) => reg || navigator.serviceWorker.register('/sw.js')).then(() => navigator.serviceWorker.ready)
}

// Состояние: настройки сервера, разрешение и подписка этого браузера
export function refreshPushState() {
  pushState.supported = pushSupported()
  pushState.permission = pushState.supported ? Notification.permission : 'denied'
  const config = isAuthenticated() ? axios.get('/push/config').then((res) => res.data).catch(() => ({ enabled: false })) : Promise.resolve({ enabled: false })
  return config.then((data) => {
    pushState.serverEnabled = !!data.enabled
    pushState.publicKey = data.public_key || ''
    if (!pushState.supported) return null
    return registration().then((reg) => reg.pushManager.getSubscription()).catch(() => null)
  }).then((subscription) => {
    pushState.subscribed = !!subscription
    pushState.endpoint = subscription ? subscription.endpoint : ''
    pushState.ready = true
    return subscription
  })
}

// Привязать подписку к текущему пользователю (и обновить «последнее открытие»)
function bind(subscription, label) {
  const json = subscription.toJSON()
  return axios.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    device_id: deviceId(),
    label: label || undefined,
    standalone: isStandalone()
  }).then((res) => res.data.device)
}

const OPT_IN_KEY = 'push_opt_in'

function optedIn() {
  try {
    return localStorage.getItem(OPT_IN_KEY) === '1'
  } catch (e) {
    return false
  }
}

function setOptIn(on) {
  try {
    if (on) localStorage.setItem(OPT_IN_KEY, '1')
    else localStorage.removeItem(OPT_IN_KEY)
  } catch (e) {
    // без запоминания: тихая переподписка не включится
  }
}

// Сообщить серверу об ответе на запрос разрешения и об установке приложения: это попадает в журнал действий
function reportPermission(state) {
  axios.post('/push/permission', { state, standalone: isStandalone() }).catch(() => {})
}

export function reportInstalled() {
  if (!isAuthenticated()) return
  axios.post('/pwa/installed', { device_id: deviceId() }).catch(() => {})
}

// Включить уведомления в этом браузере: запрос разрешения, подписка, привязка. По нажатию кнопки (на iPhone иначе нельзя).
export function enablePush(label) {
  if (!pushState.supported) return Promise.reject(new Error('Этот браузер не поддерживает push-уведомления'))
  if (!pushState.serverEnabled || !pushState.publicKey) return Promise.reject(new Error('Push-уведомления на сервере не настроены'))
  return Notification.requestPermission().then((permission) => {
    pushState.permission = permission
    // ответ на запрос разрешения — в журнал (кто, на каком устройстве, разрешил или нет)
    reportPermission(permission)
    if (permission !== 'granted') throw new Error('Браузер не дал разрешения на уведомления: включите их для этого сайта в настройках браузера')
    return registration()
  }).then((reg) => reg.pushManager.getSubscription().then((existing) => existing || reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(pushState.publicKey)
  }))).then((subscription) => bind(subscription, label).then((device) => {
    pushState.subscribed = true
    pushState.endpoint = subscription.endpoint
    setOptIn(true)
    return device
  }))
}

// Отключить уведомления в этом браузере: подписка отменяется и отвязывается на сервере
export function disablePush() {
  return registration().then((reg) => reg.pushManager.getSubscription()).then((subscription) => {
    if (!subscription) return null
    setOptIn(false)
    const endpoint = subscription.endpoint
    return axios.post('/push/unsubscribe', { endpoint }).catch(() => {}).then(() => subscription.unsubscribe()).then(() => {
      pushState.subscribed = false
      pushState.endpoint = ''
    })
  })
}

// При входе и запуске: если браузер уже подписан, привязываем его к текущему пользователю (в браузере мог войти другой сотрудник)
export function syncPush() {
  if (!isAuthenticated()) return Promise.resolve()
  // разрешение ещё не дано (или браузер не умеет push): только узнаём состояние — по нему предлагается включить уведомления (PushPrompt)
  if (!pushSupported() || Notification.permission !== 'granted') return refreshPushState().catch(() => null).then(() => null)
  return refreshPushState().then((subscription) => {
    if (!pushState.serverEnabled) return null
    if (subscription) return bind(subscription).catch(() => null)
    // браузер потерял подписку (истёк срок, очистили данные), а пользователь её включал и разрешение есть — подписываем тихо
    return optedIn() ? enablePush().catch(() => null) : null
  })
}

// Воркер сообщил, что браузер сменил подписку: привязываем заново
export function listenPushChanges() {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'PUSH_SUBSCRIPTION_CHANGED') syncPush()
  })
}
